import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Location, useLocation, useNavigate } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import { useLazyQuery, useMutation } from "@apollo/client";
import { useToast } from "@npm-telluria-tecnologia/telluria-ui";

// Molecules
import {
  FormConcretingRequestConcreteRecipe,
  FormConcretingRequestConcreting,
  FormConcretingRequestContract,
  FormConcretingRequestPump,
  MessagesDialog,
  PageHeader
} from "@molecules/index";

// Hooks
import { useAuth } from "@hooks/useAuth";

// Organisms
import StepTabs, { IStepTabProps, validateStepTabForm } from "@organisms/StepTabs";

// store
import { SetStateAction, useAtom, WritableAtom } from "@libraries/jotai";

// Languages
import useTranslation from "@languages/useTranslation";
import { ConcretingRequestMessages, CustomerMessages } from "src/languages/interfaces";

// Graphql
import utils from "@graphql/helpers/utils.graphql";
import {
  ConcretingRequestMutationsCreateArgs,
  ConcretingRequestMutationsUpdateArgs,
  ConcretingRequestType,
  ECommandResultStatus,
  EConcretingRequestStatus,
  EParameter,
  EWhereClausesOperators,
  ParamQueriesGetAllWithPaginationArgs
} from "@store/graphql/helpers/types.graphql";
import {
  concretingRequestCreate,
  concretingRequestUpdate,
  TConcretingRequestCreateMutations,
  TConcretingRequestUpdateMutations
} from "@store/graphql/groups/concretingRequest/mutation.graphql";

// Atoms
import { Loading, TabPanel } from "@atoms/index";

// Libraries
import Yup from "@libraries/yup";
import { Message } from "@libraries/icons";
import { Badge } from "@libraries/mui";

import { paramAllWithPagination, TParamAllWithPaginationQueries } from "@graphql/groups/param/query.graphql";
import * as Styled from "./styles";
import Office from "../Office";

enum EStepTabPanelIndexes {
  formContract = 0,
  formConcreting = 1,
  formConcreteRecipe = 2,
  formPump = 3
}

export interface IFormConcretingRequestInputs {
  contract: any;
  concreteBatchingPlant: any;
  concretingDate: Date | null;
  travelsReleased: number;
  interval: any;
  observationConcreting: string;
  requester: string;
  requesterPhoneNumber: string;
  contractConcreteRecipe: any;
  contractedQuantity: number;
  balanceAvailable: number;
  requestedQuantity: number;
  quantityPerConcreteMixer: number;
  pieceToBeConcreted: string;
  pump: any;
  observationPump: string;
  pipingDistance: number;
}

interface ConcretingRequestProps {
  selectedStepTabAtom: WritableAtom<number, SetStateAction<number>, void>;
}

interface LocationProps extends Location {
  state: {
    mode?: "EDIT";
    concretingRequest?: ConcretingRequestType;
    onlyView?: boolean;
  };
}

const ConcretingRequest: React.FC<ConcretingRequestProps> = ({
  selectedStepTabAtom
  // mode
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { state } = location as LocationProps;
  const mode = state?.mode || "CREATE";
  const onlyView = state?.onlyView || false;

  const [isLoading, setIsLoading] = useState(false);

  /*
   * Get the min days of programation solicitation from database
   */
  const FgetMinDaysOfDb = async () => {
    const { data } = await getParams({
      variables: {
        where: [
          {
            property: "name",
            operator: EWhereClausesOperators.EQUALS,
            value: EParameter.PROG_SOLICITATION_MIN_DAYS
          }
        ]
      }
    });

    if (data?.param.getAllWithPagination.status === "SUCCESS") {
      const result = data?.param.getAllWithPagination.result;

      return Number(result?.[0]?.key);
    }

    return 1;
  };

  const formConcretingRequestContractSchema = Yup.object().shape({
    contract: Yup.object()
      .required()
      .test(
        "is-active-contract",
        { key: "", value: "Contract must be active" },
        (value) => {
          if (!value) return false;

          return value.status === "ACTIVE";
        }
      )
  });

  const formConcretingRequestConcretingSchema = Yup.object().shape({
    concretingDate: Yup.date()
      .required()
      .test(
        "is-valid-minute",
        { key: "yup.date.minutes15", value: "Minutes must be 00, 15, 30 or 45" },
        (value) => {
          if (!value) return false;
          // verify if minutes is 00, 15, 30 or 45
          const minutes = value.getMinutes();

          return !(minutes !== 0
            && minutes !== 15
            && minutes !== 30
            && minutes !== 45);

        }
      )
      .test(
        "is-valid-date",
        {},
        async (value, testContext) => {
          if (!value) return false;

          const minDaysForSolicitation = await FgetMinDaysOfDb();

          const minDate = new Date(
            new Date().getFullYear(),
            new Date().getMonth(),
            new Date().getDate() + minDaysForSolicitation,
            0,
            0,
            0,
            0
          );

          if (value >= minDate) {
            return true;
          }

          const minDateString = minDate.toLocaleDateString("pt-BR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric"
          });

          return testContext.createError({
            // eslint-disable-next-line no-template-curly-in-string
            message: {
              key: "yup.date.min",
              values: { min: minDateString }
            }
          });
        }
      ),
    interval: Yup.string().required(),
    requester: Yup.string().trim().required(),
    requesterPhoneNumber: Yup.string().required()
      .transform((value) => value.replace(/-|_/g, ""))
      .min(14, "")
  });

  const formConcretingRequestConcreteRecipeSchema = Yup.object().shape({
    contractConcreteRecipe: Yup.object().required(),
    contractedQuantity: Yup.number(),
    // requestedQuantity need to be less than or equal to contractedQuantity
    requestedQuantity: Yup.number()
      .required()
      .moreThan(0)
      .max(Yup.ref("balanceAvailable")),
    quantityPerConcreteMixer: Yup.number().required().moreThan(0),
    pieceToBeConcreted: Yup.string().required()
  });

  const formConcretingRequestPumpSchema = Yup.object().shape({
    pipingDistance: Yup.number().required()
  });

  const formSchema = formConcretingRequestContractSchema
    .concat(formConcretingRequestConcretingSchema)
    .concat(formConcretingRequestConcreteRecipeSchema)
    .concat(formConcretingRequestPumpSchema);
  const methods = useForm<IFormConcretingRequestInputs>({
    resolver: yupResolver(formSchema),
    reValidateMode: "onChange",
    mode: "onBlur"
  });

  const steps: IStepTabProps[] = useMemo(() => [
    {
      id: "stepsConcretingRequest_contractTab",
      tabLabel: t(ConcretingRequestMessages.stepContractText),
      schema: formConcretingRequestContractSchema
    },
    {
      id: "stepsConcretingRequest_concretingTab",
      tabLabel: t(ConcretingRequestMessages.stepConcretingText),
      schema: formConcretingRequestConcretingSchema
    },
    {
      id: "stepsConcretingRequest_concreteRecipeTab",
      tabLabel: t(ConcretingRequestMessages.stepConcreteRecipeText),
      schema: formConcretingRequestConcreteRecipeSchema
    },
    {
      id: "stepsConcretingRequest_pumpTab",
      tabLabel: t(ConcretingRequestMessages.stepPumpText)
    }
  ], [t]);

  const [currentSteps, setCurrentSteps] = useState<IStepTabProps[]>(steps);

  const { addToast } = useToast();
  const navigate = useNavigate();
  const { getDecodedToken } = useAuth();
  const token = getDecodedToken();

  const [currentConcretingRequest, setConcretingRequest] = useState(
    state?.concretingRequest || undefined
  );

  const [selectedStepTab, setSelectedStepTab] = useAtom(selectedStepTabAtom);

  // Messages Dialog State
  const [messagesDialogOpen, setMessagesDialogOpen] = useState(false);
  const handleMessagesDialog = useCallback(() => {
    setMessagesDialogOpen(!messagesDialogOpen);
  }, [messagesDialogOpen]);

  const [getParams] = useLazyQuery<
    TParamAllWithPaginationQueries,
    ParamQueriesGetAllWithPaginationArgs
  >(paramAllWithPagination(["id", "name", "key"]), {
    fetchPolicy: "network-only"
  });

  const [MutationCreateConcretingRequest] = useMutation<
    TConcretingRequestCreateMutations,
    ConcretingRequestMutationsCreateArgs
  >(concretingRequestCreate(["id"]));

  const [MutationUpdateConcretingRequest] = useMutation<
    TConcretingRequestUpdateMutations,
    ConcretingRequestMutationsUpdateArgs
  >(concretingRequestUpdate(["id"]));

  const createConcretingRequest = useCallback(async () => {
    const mutation = await MutationCreateConcretingRequest({
      variables: {
        concretingRequest: {
          contractId: methods.getValues("contract").id,
          concreteBatchingPlantId: methods.getValues("concreteBatchingPlant")
            ? methods.getValues("concreteBatchingPlant").id
            : null,
          requestedDate: methods.getValues("concretingDate"),
          requester: methods.getValues("requester"),
          requesterPhoneNumber: methods.getValues("requesterPhoneNumber"),
          interval: methods.getValues("interval"),
          observationConcreting: methods.getValues("observationConcreting"),
          contractConcreteRecipeId: methods.getValues("contractConcreteRecipe")
            .id,
          requestedQuantity: utils.formatMaskStringToNumber(
            methods.getValues("requestedQuantity").toString()
          ),
          quantityPerConcreteMixer: utils.formatMaskStringToNumber(
            methods.getValues("quantityPerConcreteMixer").toString()
          ),
          pieceToBeConcreted: methods.getValues("pieceToBeConcreted"),
          pump: methods.getValues("pump"),
          observationPump: methods.getValues("observationPump"),
          pipingDistance: utils.formatMaskStringToNumber(
            methods.getValues("pipingDistance").toString()
          ),
          requestingUserId: token.primarysid
        }
      }
    });

    return mutation.data?.concretingRequest.create;
  }, [MutationCreateConcretingRequest, methods, token]);

  const updateConcretingRequest = useCallback(async () => {
    const concretingToUpdated = currentConcretingRequest as ConcretingRequestType;

    const mutation = await MutationUpdateConcretingRequest({
      variables: {
        concretingRequest: {
          id: concretingToUpdated.id,
          contractId: methods.getValues("contract").id,
          concreteBatchingPlantId: methods.getValues("concreteBatchingPlant")
            ? methods.getValues("concreteBatchingPlant").id
            : null,
          requestedDate: methods.getValues("concretingDate"),
          requester: methods.getValues("requester"),
          requesterPhoneNumber: methods.getValues("requesterPhoneNumber"),
          interval: methods.getValues("interval"),
          observationConcreting: methods.getValues("observationConcreting"),
          contractConcreteRecipeId: methods.getValues("contractConcreteRecipe")
            .id,
          requestedQuantity: utils.formatMaskStringToNumber(
            methods.getValues("requestedQuantity").toString()
          ),
          quantityPerConcreteMixer: utils.formatMaskStringToNumber(
            methods.getValues("quantityPerConcreteMixer").toString()
          ),
          pieceToBeConcreted: methods.getValues("pieceToBeConcreted"),
          pump: methods.getValues("pump"),
          observationPump: methods.getValues("observationPump"),
          pipingDistance: utils.formatMaskStringToNumber(
            methods.getValues("pipingDistance").toString()
          ),
          status:
            concretingToUpdated.status === EConcretingRequestStatus.REJECTED
              ? "PENDING"
              : concretingToUpdated.status
        }
      }
    });

    return mutation.data?.concretingRequest.update;
  }, [MutationUpdateConcretingRequest, currentConcretingRequest, methods]);

  const handleChangeStepTab = (indexNewTab: number) => validateStepTabForm(steps, methods, indexNewTab);

  const handleEndOfProcess = useCallback(async () => {
    try {
      setIsLoading(true);

      const response = mode === "CREATE"
        ? await createConcretingRequest()
        : await updateConcretingRequest();

      if (response?.status !== ECommandResultStatus.SUCCESS) return;

      addToast({
        duration: 6000,
        type: "success",
        title: t(ConcretingRequestMessages.messageUnderReview)
      });

      navigate("/concreting-requests");
    } catch (error) {
      addToast({
        type: "error",
        title: "Internal client error",
        description: error.message
      });
    } finally {
      setIsLoading(false);
    }
  }, [
    addToast,
    createConcretingRequest,
    navigate,
    updateConcretingRequest,
    mode
  ]);

  useEffect(() => () => {
    setSelectedStepTab(0);
  }, [setSelectedStepTab]);

  return (
    <Office>
      <PageHeader
        url="/concreting-requests"
        title={t(CustomerMessages.applicationConcretingRequest)}
      />

      <Styled.Container>
        {isLoading ? (
          <Loading />
        ) : (
          <>
            <StepTabs
              content={currentSteps}
              selectedStepTab={selectedStepTab}
              setSelectedStepTab={setSelectedStepTab}
              mode={mode}
              disableControlButtons={onlyView}
              onFinish={handleEndOfProcess}
              onChangeTab={handleChangeStepTab}
            >
              <TabPanel
                id="full-width-contractStepTabPanel"
                value={selectedStepTab}
                index={EStepTabPanelIndexes.formContract}
              >
                <FormConcretingRequestContract
                  controlForms={methods}
                  currentConcretingRequest={currentConcretingRequest}
                  mode={mode}
                />
              </TabPanel>

              <TabPanel
                id="full-width-concretingStepTabPanel"
                value={selectedStepTab}
                index={EStepTabPanelIndexes.formConcreting}
              >
                <FormConcretingRequestConcreting
                  controlForms={methods}
                  currentConcretingRequest={currentConcretingRequest}
                  onlyView={onlyView}
                />
              </TabPanel>

              <TabPanel
                id="full-width-concreteRecipeStepTabPanel"
                value={selectedStepTab}
                index={EStepTabPanelIndexes.formConcreteRecipe}
              >
                <FormConcretingRequestConcreteRecipe
                  controlForms={methods}
                  currentConcretingRequest={currentConcretingRequest}
                  onlyView={onlyView}
                />
              </TabPanel>

              <TabPanel
                id="full-width-pumpStepTabPanel"
                value={selectedStepTab}
                index={EStepTabPanelIndexes.formPump}
              >
                <FormConcretingRequestPump
                  controlForms={methods}
                  currentConcretingRequest={currentConcretingRequest}
                  onlyView={onlyView}
                />
              </TabPanel>
            </StepTabs>
            {currentConcretingRequest && (
              <Styled.MessagesButtonContainer>
                <Styled.MessagesButton
                  size="large"
                  onClick={handleMessagesDialog}
                >
                  <Badge
                    badgeContent={
                      currentConcretingRequest.messages?.length || 0
                    }
                    color="error"
                  >
                    <Message fontSize="large" />
                  </Badge>
                </Styled.MessagesButton>
              </Styled.MessagesButtonContainer>
            )}
          </>
        )}
      </Styled.Container>
      {currentConcretingRequest && (
        <MessagesDialog
          open={messagesDialogOpen}
          onClose={handleMessagesDialog}
          concretingRequestId={currentConcretingRequest.id}
        />
      )}
    </Office>
  );
};

export default ConcretingRequest;
